import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useNavigationGuard } from "@/hooks/useNavigationGuard";
import { useOrgPaths } from "@/hooks/useOrgPaths";
import { useExperiment, usePlots, useTasks, useTrial } from "@/hooks/useFirestore";
import { useDeleteExperiment, useDeleteTask, useUpdateTask } from "@/services/dataService";
import { Task } from "@/types/task";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Edit, Grid3X3, MapPin, MoreHorizontal, Plus, Search, Trash2 } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import CopyLinkButton from "@/components/CopyLinkButton";
import TransitionOverlay from "@/components/TransitionOverlay";
import { ensureMinDuration } from "@/utils/delay";
import { generateDuplicateTaskName } from "@/lib/utils";
import AddTaskDialog from "../components/AddTaskDialog";
import EditExperimentDialog from "../components/EditExperimentDialog";
import TaskList from "../components/TaskList";

const MIN_CREATION_TRANSITION_MS = 1500;

const ExperimentDetails = () => {
  const { trialId, experimentId } = useParams<{ trialId: string; experimentId: string }>();
  const { trial, loading: trialLoading } = useTrial(trialId);
  const { experiment, loading: experimentLoading, error: experimentError } = useExperiment(trialId, experimentId);
  const { tasks, loading: tasksLoading, error: tasksError } = useTasks(trialId, experimentId);
  const { plots, loading: plotsLoading } = usePlots(trialId, experimentId);
  const { userRole } = useAuth();
  const navigate = useNavigate();
  const paths = useOrgPaths();
  const beginNavigationGuard = useNavigationGuard();
  const { toast } = useToast();
  const [isAddTaskOpen, setIsAddTaskOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const creationStartedAtMsRef = useRef<number>(0);
  const [creationOverlay, setCreationOverlay] = useState<{
    open: boolean;
    title: string;
    subtitle?: string;
    startedAtMs: number;
  } | null>(null);
  const [taskSearchTerm, setTaskSearchTerm] = useState("");
  const [taskInitialValues, setTaskInitialValues] = useState<{
    name: string;
    description?: string;
    startDate: Date;
    entryConfigData: Array<{ entryConfigId: string; minNumSamples: number }>;
  } | undefined>(undefined);

  // Filter tasks based on search term
  const filteredTasks = useMemo(() => {
    if (!taskSearchTerm.trim()) return tasks;

    const searchLower = taskSearchTerm.toLowerCase();
    return tasks.filter((task) => {
      const nameMatch = task.name.toLowerCase().includes(searchLower);
      const descriptionMatch = task.description?.toLowerCase().includes(searchLower);
      return nameMatch || descriptionMatch;
    });
  }, [tasks, taskSearchTerm]);

  // Role-based permissions
  // canEdit controls who can create/modify items
  const canEdit = userRole && ["collector", "editor", "admin"].includes(userRole);
  // Collectors, editors, and admins can see delete UI (actual deletion enforced by backend security rules)
  const canDelete = userRole && ["collector", "editor", "admin"].includes(userRole);

  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();
  const deleteExperiment = useDeleteExperiment();

  // Show loading state
  if (trialLoading || experimentLoading) {
    return (
      <div className="container mx-auto p-4 max-w-5xl">
        <div className="flex justify-center items-center h-64">
          <p>Loading experiment...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (experimentError) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-500">Error loading experiment details: {experimentError.message}</p>
      </div>
    );
  }

  // Only redirect if we're done loading and experiment is still null
  if (!experimentLoading && !experiment) {
    return (
      <Navigate
        replace
        to={paths.trial(trialId)}
      />
    );
  }

  // At this point, experiment should exist
  if (!experiment || !trial) {
    return null;
  }

  const handleTaskCreateStart = (taskName: string, startedAtMs: number) => {
    creationStartedAtMsRef.current = startedAtMs;
    setCreationOverlay({
      open: true,
      title: "Creating task...",
      subtitle: taskName
        ? `Taking you to "${taskName}"...`
        : "Taking you to your new task...",
      startedAtMs,
    });
  };

  const handleTaskCreateError = () => {
    creationStartedAtMsRef.current = 0;
    setCreationOverlay(null);
  };

  const handleAddTask = async (task: Task) => {
    const stillHere = beginNavigationGuard();
    const startedAtMs = creationStartedAtMsRef.current || Date.now();
    await ensureMinDuration(startedAtMs, MIN_CREATION_TRANSITION_MS);
    creationStartedAtMsRef.current = 0;
    setCreationOverlay(null);

    toast({
      title: "Task created",
      description: "Your new task has been created.",
    });

    setIsAddTaskOpen(false);
    if (stillHere()) {
      navigate(paths.task(trialId, experimentId, task.id));
    }
  };

  const handleEditTask = async (updatedTask: Task) => {
    await updateTask(trialId!, experimentId!, updatedTask.id!, updatedTask);

    toast({
      title: "Task updated",
      description: `${updatedTask.name} has been updated`,
    });
  };

  // State-only change from the task list. Send ONLY { state } — never the
  // (possibly stale) full task — so a whole-task write can't rewrite
  // dataCollectionConfigs and silently strand (hide) a concurrently-added
  // trait's collected data.
  const handleTaskStateChange = async (taskId: string, newState: Task["state"]) => {
    await updateTask(trialId!, experimentId!, taskId, { state: newState });
  };

  const handleDeleteTask = async (task: Task) => {
    // Show the overlay
    setCreationOverlay({
      open: true,
      title: "Deleting task...",
      subtitle: `Removing "${task.name || "task"}"...`,
      startedAtMs: Date.now(),
    });

    try {
      await deleteTask(trialId!, experimentId!, task.id!);
      toast({
        title: "Task deleted",
        description: task.name ? `${task.name} has been deleted` : "Task has been deleted",
      });
    } catch (error) {
      toast({
        title: "Cannot delete task",
        description: error instanceof Error ? error.message : "Failed to delete the task. Please try again.",
        variant: "destructive",
      });
    } finally {
      setCreationOverlay(null);
    }
  };

  const handleDuplicateTask = (task: Task) => {
    const entryConfigData = (task.dataCollectionConfigs || [])
      .filter(config => !config.userVisibility?.length)
      .map(config => ({
        entryConfigId: config.entryConfigRef.id,
        minNumSamples: config.minNumSamples,
      }));

    setTaskInitialValues({
      name: generateDuplicateTaskName(task.name),
      description: task.description,
      startDate: new Date(),
      entryConfigData,
    });
    setIsAddTaskOpen(true);
  };

  const handleDeleteExperiment = async () => {
    // Don't manually close the dialog here - let Radix's AlertDialogAction
    // handle the close via onOpenChange to ensure proper body pointer-events cleanup.
    setCreationOverlay({
      open: true,
      title: "Deleting experiment...",
      subtitle: `Removing "${experiment.name}"...`,
      startedAtMs: Date.now(),
    });

    const stillHere = beginNavigationGuard();
    try {
      await deleteExperiment(trialId!, experimentId!);
      toast({
        title: "Experiment deleted",
        description: `${experiment.name} has been deleted`,
      });
      if (stillHere()) {
        navigate(paths.trial(trialId));
      }
    } catch (error) {
      toast({
        title: "Cannot delete experiment",
        description: error instanceof Error ? error.message : "Failed to delete the experiment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setCreationOverlay(null);
    }
  };

  return (
    <div className="container mx-auto p-4 max-w-4xl">
      <TransitionOverlay
        open={creationOverlay?.open ?? false}
        title={creationOverlay?.title ?? ""}
        subtitle={creationOverlay?.subtitle}
      />
      {/* Breadcrumb navigation */}
      <div className="mb-4">
        <Link to={paths.trial(trialId)}>
          <Button
            variant="ghost"
            size="sm"
            className="flex items-center gap-1 mb-2 text-agri-green hover:text-agri-green-dark">
            <ArrowLeft className="h-4 w-4" />
            Back to Trial {trial.name}
          </Button>
        </Link>
      </div>

      {/* Experiment Info Card */}
      <div className="relative">
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-2xl font-bold text-agri-green-dark">{experiment.name}</CardTitle>
            <div className="flex flex-shrink-0 items-center gap-1">
              <CopyLinkButton path={paths.experiment(trialId, experimentId)} />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4" />
                    <span className="sr-only">Open menu</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canEdit && (
                    <DropdownMenuItem onClick={() => setIsEditDialogOpen(true)}>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                  )}
                  {canDelete && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => setIsDeleteDialogOpen(true)}
                        className="text-destructive focus:text-destructive">
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardHeader>
          <CardContent>
            {experiment.description && (
              <p className="text-foreground mb-4">{experiment.description}</p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {experiment.location && (
                <div className="flex items-center">
                  <MapPin className="h-5 w-5 text-agri-green mr-2" />
                  <span className="text-foreground">{experiment.location}</span>
                </div>
              )}

              <div className="flex items-center">
                <Grid3X3 className="h-5 w-5 text-agri-green mr-2" />
                <span className="text-foreground">
                  {plotsLoading ? "Loading..." : `${plots.length} plots`}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tasks Section */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-agri-green">Tasks</h2>
        {canEdit && (
          <Button
            variant="default"
            onClick={() => setIsAddTaskOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Add Task
          </Button>
        )}
      </div>

      {/* Search Box - only show if there are tasks */}
      {tasks.length > 0 && (
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search tasks by name or description..."
            value={taskSearchTerm}
            onChange={(e) => setTaskSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      )}

      {tasksError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-600">Error loading tasks: {tasksError.message}</p>
        </div>
      )}

      {tasksLoading ? (
        <div className="flex justify-center items-center h-32">
          <p className="text-muted-foreground">Loading tasks...</p>
        </div>
      ) : tasks.length === 0 ? (
        <EmptyState
          title="No tasks yet"
          buttonText="Add First Task"
          onAction={() => setIsAddTaskOpen(true)}
          canEdit={canEdit}
        />
      ) : filteredTasks.length === 0 ? (
        <EmptyState
          title="No tasks match your search"
          description="Try adjusting your search terms"
        />
      ) : (
        <TaskList
          tasks={filteredTasks}
          trialId={trial.id!}
          experimentId={experimentId!}
          onEditTask={handleEditTask}
          onChangeTaskState={handleTaskStateChange}
          onDeleteTask={handleDeleteTask}
          onDuplicateTask={handleDuplicateTask}
          canEdit={canEdit}
          canDelete={canDelete}
        />
      )}

      {/* Add Task Dialog */}
      <AddTaskDialog
        open={isAddTaskOpen}
        onOpenChange={(open) => {
          setIsAddTaskOpen(open);
          if (!open) setTaskInitialValues(undefined);
        }}
        trialId={trial.id!}
        experimentId={experimentId!}
        onTaskAdded={handleAddTask}
        onCreateStart={handleTaskCreateStart}
        onCreateError={handleTaskCreateError}
        initialValues={taskInitialValues}
      />

      {/* Edit Experiment Dialog */}
      <EditExperimentDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        experiment={experiment}
        trialId={trialId!}
        onExperimentUpdated={() => {
          toast({
            title: "Experiment updated",
            description: `${experiment.name} has been updated`,
          });
        }}
        onExperimentDeleted={handleDeleteExperiment}
        canEdit={canEdit}
        canDelete={canDelete}
      />

      {/* Delete Experiment Confirmation */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the experiment "{experiment.name}" and all its associated tasks, plots, and data. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteExperiment}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ExperimentDetails;
